import { Modal, Button, Form, Row, Col, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { PdfUtils } from '../PdfUtils';

const EditPersonModal = ({ show, selectedPerson, onEdited, onClose, onExited, onNext, onPrevious, hasNext, hasPrevious }) => {
    const { t } = useTranslation();
    // State to hold the form data (editable fields)
    const [formData, setFormData] = useState({});

    useEffect(() => {
        // Set form data to selectedPerson when the modal opens
        if (selectedPerson) {
            setFormData(selectedPerson);
        }
    }, [selectedPerson]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            // Only handle keyboard navigation when the modal is visible
            if (!show) return;

            if (event.key === 'ArrowLeft' && hasPrevious) {
                event.preventDefault();
                onPrevious();
            } else if (event.key === 'ArrowRight' && hasNext) {
                event.preventDefault();
                onNext();
            }
        };

        // Add event listener when modal is shown
        if (show) {
            window.addEventListener('keydown', handleKeyDown);
        }

        // Cleanup event listener
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [show, hasNext, hasPrevious, onNext, onPrevious]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSave = () => {
        onEdited(formData);
        onClose();
    };

    const formInputField = (key) => {
        return (
            key === 'table' ? (
                <div className="form-control-plaintext w-100" style={{ whiteSpace: 'pre-wrap', marginTop: -10 }}>
                    {{
                        1: t('Latviešu strēlnieku 2. brigādes apbalvoto karavīru saraksts'),
                        2: t('Latviešu SS leģionā mobilizēto saraksts'),
                        3: t('Kritušo, bez vēsts pazudušo, ievainoto un gūstā nonākušo latviešu strēlnieku saraksts'),
                        4: t('Zedelgemas (Beļģija) karagūstekņu nometnē ieslodzīto latviešu karavīru saraksts'),
                        5: t("Latviešu strēlnieku bataljonos (pulkos) dienējušo saraksts"),
                        6: t("Kritušo un bez vēsts pazudušo Latviešu SS leģiona karavīru saraksts")
                    }[formData[key]] || t("Nav minēts")}
                </div>
            ) : (
                <Form.Control
                    as="textarea"
                    rows={2}
                    name={key}
                    value={formData[key] || ''}
                    onChange={handleInputChange}
                    placeholder={t("Ievadiet") + " " + t(key)}
                    className="w-100"
                />
            )
        );
    };

    // Render form fields in pairs (2 columns per row)
    const renderFormFields = () => {
        if (!selectedPerson) return null;

        const filteredPerson = { ...selectedPerson };
        delete filteredPerson.id;

        const entries = Object.entries(filteredPerson);

        const rows = [];

        // Process entries in pairs (0,1), (2,3), etc.
        for (let i = 0; i < entries.length; i += 2) {
            const [key1, value1] = entries[i];
            // Check if there's a second entry in this pair
            const hasSecondEntry = i + 1 < entries.length;
            const [key2, value2] = hasSecondEntry ? entries[i + 1] : [null, null];

            const title1 = key1 == 'table' ? t("Datubāze") : t(key1)
            const title2 = key2 == 'table' ? t("Datubāze") : t(key2)

            rows.push(
                <Row key={`row-${i}`} className="mb-3 w-100">
                    <Col xs={12} md={6}>
                        <Form.Group className="w-100">
                            <Form.Label style={{ fontWeight: 'bold' }}>{title1}</Form.Label>
                            {formInputField(key1)}
                        </Form.Group>
                    </Col>

                    {hasSecondEntry && (
                        <Col xs={12} md={6}>
                            <Form.Group className="w-100">
                                <Form.Label style={{ fontWeight: 'bold' }}>{title2}</Form.Label>
                                {formInputField(key2)}
                            </Form.Group>
                        </Col>
                    )}
                </Row>
            );
        }

        return rows;
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            onExited={onExited}
            centered
            size="xl"
            dialogClassName="modal-full-width"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("Informācija")}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ position: 'relative', padding: '1.5rem 5rem' }}>
                {/* Previous button - inside modal on the left */}
                <Button
                    variant="outline-secondary"
                    onClick={onPrevious}
                    disabled={!hasPrevious}
                    style={{
                        position: 'absolute',
                        left: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <FaChevronLeft />
                </Button>

                {/* Next button - inside modal on the right */}
                <Button
                    variant="outline-secondary"
                    onClick={onNext}
                    disabled={!hasNext}
                    style={{
                        position: 'absolute',
                        right: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <FaChevronRight />
                </Button>

                <Form className="w-100">
                    <Container fluid className="p-0">
                        {renderFormFields()}
                    </Container>
                </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between w-100">
                <div>
                    <Button
                        variant="link"
                        className="text-decoration-underline p-0"
                        onClick={() => PdfUtils.generatePDF(selectedPerson, t)}
                    >
                        {t("Lejupielādēt PDF")}
                    </Button>
                </div>

                <div>
                    <Button variant="secondary" onClick={onClose} className="me-2">
                        {t("Aizvērt")}
                    </Button>
                    <Button variant="primary" onClick={handleSave}>
                        {t("Saglabāt")}
                    </Button>
                </div>
            </Modal.Footer>

        </Modal>
    );
};

export default EditPersonModal;